import { Button, Typography } from '@breetometer/components';
import { AddRecipientIcon, CashIcon } from 'BreetAssets';
import { BeneficiaryList } from 'BreetComponents';
import { type IBeneficiary, viewHelpArticle } from 'BreetHelpers';
import { selectBeneficiaries, setSelectedBeneficiary, useAppDispatch, useAppSelector } from 'BreetRedux';
import { useCallback, useState } from 'react';

import type { IAutomaticSettlementDestination } from './DialogAutomaticSettlement.helpers';

const Destination = ({ updateAutoSettlement, selectedBeneficiary }: IAutomaticSettlementDestination) => {
	const beneficiaries = useAppSelector(selectBeneficiaries);

	const dispatch = useAppDispatch();

	const [selectAnotherAccount, setSelectAnotherAccount] = useState<boolean>(false);

	const selectBeneficiary = useCallback(
		(selectedBeneficiaryArgs: IBeneficiary) => () => {
			dispatch(setSelectedBeneficiary(selectedBeneficiaryArgs));
		},
		[dispatch]
	);

	const useAnotherAccount = useCallback(() => {
		setSelectAnotherAccount(true);
	}, []);

	const Icon = selectAnotherAccount ? AddRecipientIcon : CashIcon;

	const headerText = selectAnotherAccount ? 'Add Settlement Account' : 'From Crypto to Your Bank Account - Automatically.';

	const isSelectedBeneficiaryEnabled = !!selectedBeneficiary?.autoSettlement;

	const buttonTxt = isSelectedBeneficiaryEnabled ? 'Disable' : 'Enable';
	const buttonVariant = isSelectedBeneficiaryEnabled ? 'tertiary' : 'primary';

	const wrapperClass = `automaticsettlement_destination${selectAnotherAccount ? ' hide-extra-padding' : ''}`;
	const iconClass = `automaticsettlement_destination_cashicon${selectAnotherAccount ? ' selectanotheraccount' : ''}`;
	const ctaClass = `automaticsettlement_destinationcta${selectAnotherAccount ? ' selectanotheraccount' : ''}`;

	const filterBeneficiaries = (i: IBeneficiary, index: number) => (selectAnotherAccount ? i : index < 1);
	const filterAutoSettlementBeneficiaries = (i: IBeneficiary) => (selectAnotherAccount ? i : i.autoSettlement);

	const filteredBeneficiaries = beneficiaries.filter((i) => i.type !== 'virtual_account').filter(filterBeneficiaries);
	const filteredAutoSettlementBeneficiaries = beneficiaries
		.filter((i) => i.type !== 'virtual_account')
		.filter(filterAutoSettlementBeneficiaries);

	return (
		<div className={wrapperClass}>
			<Icon className={iconClass} />

			<Typography
				as='h5'
				className='color-neutral-50'
			>
				{headerText}
			</Typography>

			{!selectAnotherAccount && (
				<div className='automaticsettlement_destination_description'>
					<Typography
						as='span'
						className='color-primary-50'
					>
						We can send your crypto payments to your bank account automatically without you opening the app.&nbsp;
					</Typography>
					<Typography
						as='a'
						onClick={viewHelpArticle('12621462')}
					>
						How it works.
					</Typography>
				</div>
			)}

			<BeneficiaryList
				beneficiaries={filteredAutoSettlementBeneficiaries.length ? filteredAutoSettlementBeneficiaries : filteredBeneficiaries}
				handleBeneficiarySelection={selectBeneficiary}
				selectedBeneficiaryId={selectedBeneficiary?._id}
				hideHeader={!selectAnotherAccount}
			/>

			<div className={ctaClass}>
				<Button
					label={buttonTxt}
					variant={buttonVariant}
					onClick={updateAutoSettlement}
					disabled={!selectedBeneficiary}
				/>
				{!selectAnotherAccount && beneficiaries.length > 1 && (
					<Button
						label='Use another account'
						variant='outline'
						onClick={useAnotherAccount}
					/>
				)}
			</div>
		</div>
	);
};

export default Destination;
